import { useState } from 'react';
import type { InvoiceBreakdownLine } from '@salonomy/types';
import { Avatar, Pill, Spinner } from '@salonomy/ui';
import { useInvoiceBreakdown } from '../../data/billing.js';
import { initials, money, percentFromBps, pluralize } from '../../lib/format.js';
import { billingDate } from './format.js';

/**
 * The "why is this invoice this amount" view (Overview §6.7; brief 10 §3) — the trust surface this whole
 * screen exists to prove. Every figure is read from the server /breakdown; NOTHING is computed on the
 * client (no money, no VAT, no proration — ADR-0010 §7). Each charge line drills to its provenance:
 *
 *   base         → the billing_plans row the price came from (plan code).
 *   per_chair    → the named staff the chairs×rate line was computed from (the count WITH the people).
 *   comms_overage → the per-message audit ids the overage units drill to (replayed from the send log).
 *   vat          → Salonomy's OWN UK VAT, a single derived line on the taxable subtotal.
 *
 * INVARIANT (server-asserted): Σ charge lines + VAT = total. We show exactly the server's subtotal /
 * VAT / total — we never re-sum to "check" it, because the server is the source of truth.
 */
export function InvoiceBreakdown({ invoiceId }: { invoiceId: string }) {
  const { data: bd, isLoading, isError } = useInvoiceBreakdown(invoiceId);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
        <Spinner size={16} />
        <span className="muted" style={{ fontSize: 13 }}>
          Loading the breakdown…
        </span>
      </div>
    );
  }
  if (isError || !bd) {
    return (
      <div className="muted" style={{ fontSize: 13 }}>
        Couldn’t load this invoice’s breakdown. Every figure is read live from your billing data —
        try again in a moment.
      </div>
    );
  }

  const chargeLines = bd.lines.filter((l) => l.kind !== 'vat');

  return (
    <div>
      <div className="muted" style={{ fontSize: 12.5, marginBottom: 10 }}>
        {bd.periodStart && bd.periodEnd
          ? `For ${billingDate(bd.periodStart)} – ${billingDate(bd.periodEnd)}. `
          : ''}
        Every line below traces back to logged usage — if we can’t show the receipts, we don’t bill
        it.
      </div>

      <div className="billing-breakdown">
        {chargeLines.map((line, i) => (
          <BreakdownLine key={`${line.kind}-${i}`} line={line} />
        ))}
      </div>

      {/* Totals — the server's subtotal, its single derived VAT line, and the grand total. */}
      <div className="billing-totals">
        <div className="billing-totals__row">
          <span className="muted">Subtotal (ex-VAT)</span>
          <span>{money(bd.subtotalPence)}</span>
        </div>
        <div className="billing-totals__row">
          <span className="muted">VAT {percentFromBps(bd.vatBps)}</span>
          <span>{money(bd.vatPence)}</span>
        </div>
        <div className="billing-totals__row billing-totals__row--grand">
          <span>Total</span>
          <span>{money(bd.totalPence)}</span>
        </div>
      </div>

      <div className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
        VAT is Salonomy’s own UK VAT, charged on our subscription — it is unrelated to your salon’s
        VAT settings.
      </div>
    </div>
  );
}

/** One breakdown line + its provenance drill-down (the receipts behind the amount). */
function BreakdownLine({ line }: { line: InvoiceBreakdownLine }) {
  const [open, setOpen] = useState(false);
  const prov = line.provenance;
  const hasStaff = prov.source === 'staff_chairs' && prov.staff.length > 0;
  const hasMessages = prov.source === 'comms_audit' && prov.messageIds.length > 0;
  const drillable = hasStaff || hasMessages;

  return (
    <div className="billing-line">
      <div className="billing-line__row">
        <div className="billing-line__desc">{line.description}</div>
        <div className="billing-line__amount">{money(line.amountPence)}</div>
      </div>

      {line.quantity > 1 ? (
        <div className="billing-line__meta">
          {pluralize(line.quantity, 'unit')} × {money(line.unitPricePence)}
        </div>
      ) : null}

      {prov.source === 'plan' && prov.planCode ? (
        <div className="billing-line__meta">
          From your <span className="mono">{prov.planCode}</span> plan.
        </div>
      ) : null}

      {drillable ? (
        <button
          type="button"
          className="slm-btn slm-btn--ghost slm-btn--sm"
          style={{ marginTop: 6, paddingLeft: 0 }}
          onClick={() => setOpen((v) => !v)}
        >
          {open
            ? 'Hide the receipts'
            : hasStaff
              ? `Show the ${pluralize(prov.staff.length, 'team member')} behind this`
              : `Show the ${pluralize(prov.messageIds.length, 'message')} behind this`}
        </button>
      ) : null}

      {open && hasStaff ? (
        <div className="billing-line__drill">
          <div className="muted" style={{ fontSize: 11.5, marginBottom: 8 }}>
            The chairs on this line are these active team members:
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {prov.staff.map((s) => (
              <div key={s.staffId} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Avatar initials={initials(s.displayName)} tone="slate" size={26} />
                <span style={{ fontSize: 12.5 }}>{s.displayName}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {open && hasMessages ? (
        <div className="billing-line__drill">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span className="muted" style={{ fontSize: 11.5 }}>
              Each overage unit is one logged message — the audit ids it drills to:
            </span>
            <Pill tone="soft" className="mono">
              {pluralize(prov.messageIds.length, 'message')}
            </Pill>
          </div>
          {prov.periodStart && prov.periodEnd ? (
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              Sent {billingDate(prov.periodStart)} – {billingDate(prov.periodEnd)}.
            </div>
          ) : null}
          <div className="billing-msgids">
            {prov.messageIds.map((id) => (
              <span key={id} className="billing-msgids__id" title={id}>
                {id.slice(0, 8)}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
